import { queryClient } from '@/lib/api-client.ts'
import { currentUserQuery } from '@/lib/actions.ts'
import handleApiErr from '@/lib/helpers/handleApiGerError.ts'
import { CurrStageData, currStageQuery } from '@/lib/actions/getCurrStage.ts'
import { currStageIssueTypesQuery } from '@/lib/actions/getStageIssuesTypes.ts'
import {
  GetProjectsParticipationsReturnT,
  userProjectParticipationsQuery,
} from '@/lib/actions/getUserProjectParticipations.ts'
import { currStageUsersQuery } from '@/lib/actions/getCurrStageUsers.ts'
import { ProjectStagesUsersT } from '@/components/common/menu/lib/types/projectStagesUsers.ts'
import { IssueTypeT } from '@/components/common/menu/lib/types/navigationDataTypes.ts'
import { LoaderFunctionArgs } from 'react-router-dom'
import { validateLastActiveStageId } from '@/routes/login/validateLastActiveStageId.tsx'
import { editUser } from '@/routes/settings/account/actions.ts'
import { offlineSafeEnsureQueryData } from '@/lib/helpers/offlineSafeQuery.ts'
import { InfiniteData } from '@tanstack/react-query'

// Dev-only debug logging
const isDev = import.meta.env.DEV
const debug = (...args: unknown[]) => isDev && console.log('[rootLoader]', ...args)

export type RootLoaderT = {
  stageId: string | number | undefined | null
  userId: number | undefined
  stageUsers: ProjectStagesUsersT | null | undefined
  stageIssueTypes: IssueTypeT[] | null | undefined
  stage: CurrStageData | null | undefined
} | null

export const rootLoader = async ({
  params,
}: LoaderFunctionArgs<{ projectStageId?: string }>): Promise<RootLoaderT | Response> => {
  try {
    const participationsQueryOptions = userProjectParticipationsQuery()
    const participationsQueryKey = participationsQueryOptions.queryKey

    // Handle participations with offline support
    let participationsData: InfiniteData<GetProjectsParticipationsReturnT | undefined> | undefined

    if (!navigator.onLine) {
      // Offline: use cached data only
      participationsData = queryClient.getQueryData(participationsQueryKey)
      if (!participationsData) {
        debug('Offline with no cached participations')
        return null
      }
    } else {
      // Online: try to fetch, fallback to cache
      try {
        participationsData = await queryClient.fetchInfiniteQuery(participationsQueryOptions)
      } catch {
        participationsData = queryClient.getQueryData(participationsQueryKey)
        if (!participationsData) throw new Error('No participations data')
      }
    }

    const userProjectParticipations = participationsData?.pages[0]?.projectParticipations

    // Handle user with offline support
    const userQuery = currentUserQuery()
    const userMe = await offlineSafeEnsureQueryData(queryClient, userQuery.queryKey, userQuery.queryFn)

    // If offline with no cached user, return null
    if (!userMe) {
      debug('No user data available (offline with no cache)')
      return null
    }

    const userId = userMe.id

    debug('Executed, user active project stage:', userMe.activeProjectStage)

    let stageId = validateLastActiveStageId(userProjectParticipations, userMe.activeProjectStage)

    if (params?.projectStageId) stageId = params.projectStageId

    if (userProjectParticipations?.length === 0 || !stageId)
      return {
        stageId,
        userId: userId,
        stageUsers: null,
        stageIssueTypes: null,
        stage: null,
      }

    // Fetch stage data with offline support
    const stageUsersQuery = currStageUsersQuery(stageId)
    const stageIssueTypesQueryOptions = currStageIssueTypesQuery(stageId)
    const stageQueryOptions = currStageQuery(stageId, userId)
    try {
      const [stageUsers, stageIssueTypes, stage] = await Promise.all([
        offlineSafeEnsureQueryData(queryClient, stageUsersQuery.queryKey, stageUsersQuery.queryFn),
      offlineSafeEnsureQueryData(
        queryClient,
        stageIssueTypesQueryOptions.queryKey,
        stageIssueTypesQueryOptions.queryFn,
      ),
        offlineSafeEnsureQueryData(queryClient, stageQueryOptions.queryKey, stageQueryOptions.queryFn),
      ])

      return {
        stageId,
        userId: userId,
        stageUsers,
        stageIssueTypes,
        stage,
      }
    } catch {
      await editUser(userId, { activeProjectStage: null })
      return {
        stageId: null,
        userId: userId,
        stageUsers: null,
        stageIssueTypes: null,
        stage: null,
      }
    }
  } catch (err) {
    // Check if this is a network-related error (offline or connectivity issue)
    const isNetworkError =
      !navigator.onLine ||
      (err instanceof Error &&
        (err.message.toLowerCase().includes('network') ||
          err.message.toLowerCase().includes('failed to fetch') ||
          (err as { code?: string }).code === 'ERR_NETWORK'))

    if (isNetworkError) {
      debug('Network error, returning null to allow offline mode')
      return null
    }

    debug('Error in root:', err)
    return handleApiErr(err)
  }
}
